import { Component } from '@angular/core';
import {
  ActivitiesByDayOfWeekDashboardVm,
  CategoryCountDashboardVm,
  ConnectionDashboardVm,
  PostedQuestionAndResponseDashboardVm,
  UserAgeDashboardVm,
  UserGenderDashboardVm,
  UserLastSeenDashboardVm,
} from 'src/app/models/backoffice';
import { ServerService } from 'src/app/services/server/server.service';

@Component({
    selector: 'app-main-page',
    templateUrl: './main-page.component.html',
    styleUrls: ['./main-page.component.scss'],
    standalone: false
})
export class MainPageComponent {
  /** Stable axis title objects — inline `{ text: '…' }` in templates creates a new ref every CD and forces chart redraws. */
  readonly titleAge = { text: 'Age' };
  readonly titleUsers = { text: 'Users' };
  readonly titleLastSeen = { text: 'Last seen' };
  readonly titleHour = { text: 'Hour of day' };
  readonly titleCount = { text: 'Count' };
  readonly titleDay = { text: 'Day' };
  readonly titleActivities = { text: 'Activities' };

  /** Stable format strings for chart / axis / tooltip (avoid new string refs each CD). */
  readonly fmtN0 = '{0:n0}';

  /** Cached Kendo inputs — must keep the same array/object refs across CD cycles or tooltips flicker. */
  ageChartCategories: number[] = [];
  ageChartMale: number[] = [];
  ageChartFemale: number[] = [];
  ageChartOther: number[] = [];
  ageShowMale = false;
  ageShowFemale = false;
  ageShowOther = false;

  genderChartData: Array<{ category: string; value: number; color: string }> = [];

  lastSeenChartData: Array<UserLastSeenDashboardVm & { color: string }> = [];

  postedChartCategories: string[] = [];
  postedQuestionSeries: number[] = [];
  postedResponseSeries: number[] = [];

  activitiesChartCategories: string[] = [];
  activitiesChartSeries: number[] = [];

  dashboard: ConnectionDashboardVm = {
    total_connections: 0,
    total_open_connections: 0,
    total_closed_connections: 0,
    total_joining_connections: 0,
  };
  ageDashboard: UserAgeDashboardVm[] = [];
  genderDashboard: UserGenderDashboardVm = { male_count: 0, female_count: 0, other_count: 0 };
  lastSeenDashboard: UserLastSeenDashboardVm[] = [];
  categoryCountDashboard: CategoryCountDashboardVm[] = [];
  postedQuestionAndResponseDashboard: PostedQuestionAndResponseDashboardVm[] = [];
  activitiesByDayOfWeekDashboard: ActivitiesByDayOfWeekDashboardVm[] = [];

  loadingConnectionStats = false;
  loadingAge = false;
  loadingGender = false;
  loadingLastSeen = false;
  loadingCategoryCount = false;
  loadingPostedQuestionAndResponse = false;
  loadingActivitiesByDayOfWeek = false;

  constructor(private server: ServerService) {
    this.refreshConnectionStats();
    this.refreshAgeDashboard();
    this.refreshGenderDashboard();
    this.refreshLastSeenDashboard();
    this.refreshCategoryCountDashboard();
    this.refreshPostedQuestionAndResponseDashboard();
    this.refreshActivitiesByDayOfWeekDashboard();
  }

  refreshConnectionStats() {
    this.loadingConnectionStats = true;
    this.server.onGetConnectionDashboard().subscribe({
      next: (res) => {
        if (res.success === true && res.obj) {
          this.dashboard = res.obj as ConnectionDashboardVm;
        }
        this.loadingConnectionStats = false;
      },
      error: (err) => {
        console.log(err);
        this.loadingConnectionStats = false;
      },
    });
  }

  refreshAgeDashboard() {
    this.loadingAge = true;
    this.server.onGetUserAgeDashboard().subscribe({
      next: (res) => {
        if (res.success === true && Array.isArray(res.obj)) {
          this.ageDashboard = (res.obj as UserAgeDashboardVm[])
            .filter((x) => x?.age !== null && x?.age !== undefined)
            .sort((a, b) => a.age - b.age);
        } else {
          this.ageDashboard = [];
        }
        this.rebuildAgeChartCaches();
        this.loadingAge = false;
      },
      error: (err) => {
        console.log(err);
        this.loadingAge = false;
      },
    });
  }

  refreshGenderDashboard() {
    this.loadingGender = true;
    this.server.onGetUserGenderDashboard().subscribe({
      next: (res) => {
        if (res.success === true && res.obj) {
          this.genderDashboard = res.obj as UserGenderDashboardVm;
        } else {
          this.genderDashboard = { male_count: 0, female_count: 0, other_count: 0 };
        }
        this.rebuildGenderChartCaches();
        this.loadingGender = false;
      },
      error: (err) => {
        console.log(err);
        this.loadingGender = false;
      },
    });
  }

  refreshLastSeenDashboard() {
    this.loadingLastSeen = true;
    this.server.onGetUserLastSeenDashboard().subscribe({
      next: (res) => {
        if (res.success === true && Array.isArray(res.obj)) {
          this.lastSeenDashboard = res.obj as UserLastSeenDashboardVm[];
        } else {
          this.lastSeenDashboard = [];
        }
        this.rebuildLastSeenChartCaches();
        this.loadingLastSeen = false;
      },
      error: (err) => {
        console.log(err);
        this.loadingLastSeen = false;
      },
    });
  }

  refreshCategoryCountDashboard() {
    this.loadingCategoryCount = true;
    this.server.onGetCategoryCountDashboard().subscribe({
      next: (res) => {
        if (res.success === true && Array.isArray(res.obj)) {
          this.categoryCountDashboard = (res.obj as CategoryCountDashboardVm[])
            .sort((a, b) => (b.category_count || 0) - (a.category_count || 0));
        } else {
          this.categoryCountDashboard = [];
        }
        this.loadingCategoryCount = false;
      },
      error: (err) => {
        console.log(err);
        this.loadingCategoryCount = false;
      },
    });
  }

  refreshPostedQuestionAndResponseDashboard() {
    this.loadingPostedQuestionAndResponse = true;
    this.server.onGetPostedQuestionAndResponseDashboard().subscribe({
      next: (res) => {
        if (res.success === true && Array.isArray(res.obj)) {
          this.postedQuestionAndResponseDashboard = (res.obj as PostedQuestionAndResponseDashboardVm[])
            .sort((a, b) => (a.hour_posted || 0) - (b.hour_posted || 0));
        } else {
          this.postedQuestionAndResponseDashboard = [];
        }
        this.rebuildPostedChartCaches();
        this.loadingPostedQuestionAndResponse = false;
      },
      error: (err) => {
        console.log(err);
        this.loadingPostedQuestionAndResponse = false;
      },
    });
  }

  refreshActivitiesByDayOfWeekDashboard() {
    this.loadingActivitiesByDayOfWeek = true;
    this.server.onGetActivitiesByDayOfWeek().subscribe({
      next: (res) => {
        if (res.success === true && Array.isArray(res.obj)) {
          this.activitiesByDayOfWeekDashboard = res.obj as ActivitiesByDayOfWeekDashboardVm[];
        } else {
          this.activitiesByDayOfWeekDashboard = [];
        }
        this.rebuildActivitiesChartCaches();
        this.loadingActivitiesByDayOfWeek = false;
      },
      error: (err) => {
        console.log(err);
        this.loadingActivitiesByDayOfWeek = false;
      },
    });
  }

  private rebuildAgeChartCaches(): void {
    const ages = this.ageDashboard
      .map((x) => x.age)
      .filter((x) => x !== null && x !== undefined);
    const categories = Array.from(new Set(ages)).sort((a, b) => a - b);

    this.ageChartCategories = categories;
    this.ageShowMale = this.ageDashboard.some((x) => (x.gender || '').toLowerCase() === 'male');
    this.ageShowFemale = this.ageDashboard.some((x) => (x.gender || '').toLowerCase() === 'female');
    this.ageShowOther = this.ageDashboard.some((x) => (x.gender || '').toLowerCase() === 'other');

    const seriesFor = (g: string) =>
      categories.map((age) => {
        const match = this.ageDashboard.find(
          (x) => x.age === age && (x.gender || '').toLowerCase() === g
        );
        return match?.age_count || 0;
      });

    this.ageChartMale = this.ageShowMale ? seriesFor('male') : [];
    this.ageChartFemale = this.ageShowFemale ? seriesFor('female') : [];
    this.ageChartOther = this.ageShowOther ? seriesFor('other') : [];
  }

  private rebuildGenderChartCaches(): void {
    const points = [
      { category: 'Male', value: this.genderDashboard.male_count || 0, color: '#003566' },
      { category: 'Female', value: this.genderDashboard.female_count || 0, color: '#ffc300' },
      { category: 'Other', value: this.genderDashboard.other_count || 0, color: '#005f73' },
    ];
    this.genderChartData = points.filter((x) => x.value > 0);
  }

  private rebuildLastSeenChartCaches(): void {
    this.lastSeenChartData = this.lastSeenDashboard.map((item) => {
      const key = (item.last_seen || '').toLowerCase();
      let color = '#001d3d';
      if (key.includes('hour')) {
        color = '#ffd60a';
      } else if (key.includes('24') || key.includes('day')) {
        color = '#ffc300';
      } else if (key.includes('week')) {
        color = '#003566';
      } else if (key.includes('month')) {
        color = '#005f73';
      } else if (key.includes('long')) {
        color = '#001219';
      }
      return { ...item, color };
    });
  }

  private rebuildPostedChartCaches(): void {
    this.postedChartCategories = this.postedQuestionAndResponseDashboard.map((x) => `${x.hour_posted}`);
    this.postedQuestionSeries = this.postedQuestionAndResponseDashboard.map((x) => x.question_count || 0);
    this.postedResponseSeries = this.postedQuestionAndResponseDashboard.map((x) => x.response_count || 0);
  }

  private rebuildActivitiesChartCaches(): void {
    this.activitiesChartCategories = this.activitiesByDayOfWeekDashboard.map((x) => x.day_of_week);
    this.activitiesChartSeries = this.activitiesByDayOfWeekDashboard.map((x) => x.total_count || 0);
  }

  showPointValue(event: any) {
    const seriesName = event?.series?.name || 'Value';
    const hasCategory = event?.category !== undefined && event?.category !== null;
    const categoryText = hasCategory ? ` (${event.category})` : '';
    const value = event?.value ?? 0;
    window.alert(`${seriesName}${categoryText}: ${value}`);
  }

}
